import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Drawer,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import ArrowForwardIosRoundedIcon from '@mui/icons-material/ArrowForwardIosRounded';
import { useLocation, useNavigate } from 'react-router-dom';

import { getCartCount, getCartTotal, hasCartPricingIssues } from '../../services/cartService';
import { formatCurrency } from '../../services/productSelection';
import { useAppSelector } from '../../store/hooks';

const hiddenRoutes = new Set(['/cart', '/checkout']);

const getProductWord = (count: number) => {
  const mod10 = count % 10;
  const mod100 = count % 100;

  if (mod10 === 1 && mod100 !== 11) {
    return 'товар';
  }

  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) {
    return 'товара';
  }

  return 'товаров';
};

const FloatingCartSummary: React.FC = () => {
  const [open, setOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'), { noSsr: true });
  const location = useLocation();
  const navigate = useNavigate();
  const cartItems = useAppSelector((state) => state.cart.items);

  const cartCount = getCartCount(cartItems);
  const cartTotal = getCartTotal(cartItems);
  const hasPricingIssues = hasCartPricingIssues(cartItems);

  if (isMobile || cartCount === 0 || hiddenRoutes.has(location.pathname)) {
    return null;
  }

  const goTo = (path: string) => {
    setOpen(false);
    navigate(path);
  };

  return (
    <>
      <Paper
        elevation={10}
        onClick={() => setOpen(true)}
        sx={{
          position: 'fixed',
          right: { xs: 16, md: 24 },
          bottom: { xs: 96, md: 112 },
          zIndex: 1200,
          px: 2,
          py: 1.5,
          borderRadius: 999,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          minWidth: { xs: 'calc(100vw - 32px)', sm: 320 },
          maxWidth: { xs: 'calc(100vw - 32px)', sm: 360 },
          boxShadow: '0 18px 45px rgba(15, 23, 42, 0.18)',
          background: 'linear-gradient(135deg, #1f5fbf 0%, #3278e2 100%)',
          color: '#fff',
        }}
      >
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: '50%',
            display: 'grid',
            placeItems: 'center',
            backgroundColor: 'rgba(255,255,255,0.16)',
            flexShrink: 0,
          }}
        >
          <ShoppingCartOutlinedIcon />
        </Box>

        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 13, opacity: 0.85 }}>
            {cartCount} {getProductWord(cartCount)} в корзине
          </Typography>
          <Typography sx={{ fontSize: { xs: 18, md: 20 }, fontWeight: 800 }}>
            {formatCurrency(cartTotal)}
          </Typography>
        </Box>

        <ArrowForwardIosRoundedIcon sx={{ fontSize: 18, opacity: 0.9 }} />
      </Paper>

      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 420 },
            p: 3,
          },
        }}
      >
        <Stack spacing={2.5}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>
              Корзина под рукой
            </Typography>
            <Typography color="text.secondary">
              Вы можете продолжить выбор товаров, а когда будете готовы, перейти к проверке заказа или сразу к оформлению.
            </Typography>
          </Box>

          <Paper
            variant="outlined"
            sx={{ p: 2, borderRadius: 3, backgroundColor: 'rgba(31, 95, 191, 0.04)' }}
          >
            <Stack spacing={1.25}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                <Typography color="text.secondary">Товаров</Typography>
                <Typography sx={{ fontWeight: 700 }}>{cartCount}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                <Typography color="text.secondary">Итого по товарам</Typography>
                <Typography sx={{ fontWeight: 800 }}>{formatCurrency(cartTotal)}</Typography>
              </Box>
            </Stack>
          </Paper>

          {hasPricingIssues && (
            <Alert severity="warning">
              Для части позиций цена еще не заполнена. Корзина сохранится, но оформить заказ получится после уточнения стоимости.
            </Alert>
          )}

          <Button variant="contained" size="large" onClick={() => goTo('/cart')}>
            Проверить корзину
          </Button>
          <Button
            variant="outlined"
            size="large"
            onClick={() => goTo('/checkout')}
            disabled={hasPricingIssues}
          >
            Перейти к оформлению
          </Button>
        </Stack>
      </Drawer>
    </>
  );
};

export default FloatingCartSummary;
